import React, { useState, useMemo, useEffect } from 'react';
import { Search, Eye, Download, MapPin, CreditCard, Bike, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { money, ORDER_STATUS, ACTIVE_STATUSES } from '../lib/constants';
import { PageHeader, Card, Table, Tr, Td, StatusBadge, Avatar, FilterChips, SimpleBadge } from './shared';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { EvidenceGallery } from '../components/DeliveryEvidence';
import { OrderPayment } from '../components/OrderPayment';
import { PaymentDestination } from '../components/PaymentDestination';

const PayBadge = ({ s }) => <SimpleBadge tone={s === 'approved' ? 'green' : s === 'cash' ? 'blue' : s === 'pending' ? 'amber' : 'red'}>{s === 'approved' ? 'Verificado' : s === 'cash' ? 'Efectivo' : s === 'pending' ? 'Pendiente' : 'Rechazado'}</SimpleBadge>;

export const OrderDetail = ({ order, onClose }) => {
  const { drivers, updateOrder, deleteOrder, setDriverFee, driverPay } = useApp();
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fee, setFee] = useState('');
  useEffect(() => { setFee(order ? String(order.driverFee ?? '') : ''); }, [order?.id, order?.driverFee]);
  if (!order) return null;
  const approved = drivers.filter((d) => d.approval === 'approved' && d.status === 'active');
  const subtotal = (order.items || []).reduce((s, i) => s + i.price * i.qty, 0);
  const remove = async () => {
    setBusy(true);
    try { await deleteOrder(order.id, reason); setConfirming(false); setReason(''); onClose(); }
    catch { /* toast by context */ } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!order} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="order-detail-sheet">
        <SheetHeader><SheetTitle className="text-xl font-extrabold">Pedido #{order.id}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3"><Avatar src={order.avatar} name={order.customer} size={44} /><div><p className="font-bold">{order.customer}</p><p className="text-xs text-gray-500">{order.type} • {order.date} {order.time}</p></div></div>
            <StatusBadge status={order.status} />
          </div>
          <div className="bg-[#F7F8FA] rounded-xl p-4 space-y-2 text-sm">
            <p className="font-semibold text-[#1E2130]">{order.restaurant}</p>
            {order.items.map((i, idx) => <div key={idx} className="flex justify-between text-gray-700"><span>{i.qty}x {i.name}</span><span>{money(i.price * i.qty)}</span></div>)}
            <div className="border-t pt-2 flex justify-between text-gray-600"><span>Subtotal</span><span>{money(subtotal)}</span></div>
            <div className="flex justify-between text-gray-600"><span>{order.type === 'Mandadito' ? 'Tarifa del servicio' : 'Costo de envío'}</span><span>{money(order.deliveryFee)}</span></div>
            <div className="flex justify-between font-bold text-base"><span>Total</span><span>{money(order.total)}</span></div>
          </div>
          <div className="space-y-3 text-sm">
            <p className="flex items-start gap-2"><MapPin size={16} className="text-[#EE3B36] mt-0.5" /><span><span className="font-semibold">Dirección:</span> {order.address}</span></p>
            <p className="flex items-start gap-2"><CreditCard size={16} className="text-[#2F80ED] mt-0.5" /><span><span className="font-semibold">Pago:</span> {order.payment} <span className="ml-1 inline-block align-middle"><PayBadge s={order.paymentStatus} /></span></span></p>
            {order.notes && <p className="text-gray-600"><span className="font-semibold">Notas:</span> {order.notes}</p>}
            {order.orderMode === 'pickup' && <p className="flex items-center gap-2 text-[#2F80ED] font-semibold" data-testid="admin-pickup-info">Recoger en tienda • Orden #{order.pickupCode}</p>}
            {order.status === 'awaiting_payment' && <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs font-semibold">Este pedido está retenido hasta que verifiques la transferencia en Pagos y comprobantes.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1.5">Estado</p>
              <Select value={order.status} onValueChange={(v) => updateOrder(order.id, { status: v }).then(() => toast.success('Estado actualizado'))}>
                <SelectTrigger data-testid="order-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ORDER_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1.5">Conductor</p>
              <Select value={order.driverId || 'none'} onValueChange={(v) => updateOrder(order.id, { driverId: v === 'none' ? null : v }).then((o) => toast.success(o.driver ? `Asignado a ${o.driver}` : 'Conductor removido'))}>
                <SelectTrigger data-testid="order-driver-select"><SelectValue placeholder="Asignar" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Sin asignar</SelectItem>{approved.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {order.driver && <p className="text-sm flex items-center gap-2 text-gray-600"><Bike size={16} /> Asignado a <span className="font-semibold text-[#1E2130]">{order.driver}</span></p>}
          {order.orderMode !== 'pickup' && (
            <div data-testid="order-driver-fee" className="rounded-xl border border-gray-200 p-3.5 space-y-2">
              <p className="text-sm font-semibold">Pago al conductor</p>
              <p className="text-xs text-gray-500">
                Actual: <span className="font-bold text-[#1E2130]">{money(order.driverFee || 0)}</span>
                {order.driverFeeSource === 'manual' ? ' · asignado manualmente' : driverPay?.enabled ? ` · automático (${money(driverPay.perOrderFee)} por pedido)` : ' · cálculo anterior (envío + L30)'}
              </p>
              <div className="flex gap-2">
                <Input data-testid="driver-fee-input" type="number" min="0" step="1" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Monto a pagar (L)" className="max-w-[180px]" />
                <Button data-testid="driver-fee-save" variant="outline" disabled={fee === '' || Number(fee) < 0} onClick={() => setDriverFee(order.id, Number(fee))}>Asignar pago</Button>
              </div>
              {(order.rejectedBy || []).length > 0 && (
                <p data-testid="order-rejections" className="text-xs text-red-600">Rechazado por: {order.rejectedBy.map((r) => `${r.driverName || r.driverId}${r.reason === 'timeout' ? ' (no aceptó a tiempo)' : ''}`).join(', ')}</p>
              )}
              {order.assignment?.status === 'pending' && (
                <p data-testid="order-assignment" className="text-xs text-amber-700">Asignado automáticamente a {order.assignment.driverName} · vence {new Date(order.assignment.expiresAt).toLocaleTimeString('es-HN')}</p>
              )}
            </div>
          )}
          <OrderPayment order={order} testId="admin-order-payment" />
          <PaymentDestination order={order} testId="admin-order-payment-destination" />
          {order.driverConfirmation?.confirmedAt && <p data-testid="admin-driver-confirmation" className="text-xs text-gray-500">Confirmado por {order.driver} · {new Date(order.driverConfirmation.confirmedAt).toLocaleString('es-HN')}</p>}
          <EvidenceGallery order={order} testId="admin-order-evidence" />
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cerrar</Button>
            <Button className="flex-1 bg-[#EE3B36] hover:bg-[#D93129]" data-testid="cancel-order-btn" onClick={() => updateOrder(order.id, { status: 'cancelled' }).then(() => { toast('Pedido cancelado'); onClose(); })} disabled={!ACTIVE_STATUSES.includes(order.status)}>Cancelar pedido</Button>
          </div>
          <Button data-testid="delete-order-btn" variant="outline" className="w-full border-red-300 text-red-600 hover:bg-red-50 gap-2" onClick={() => setConfirming(true)}><Trash2 size={16} /> Eliminar pedido</Button>
          <AlertDialog open={confirming} onOpenChange={setConfirming}>
            <AlertDialogContent data-testid="delete-order-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar el pedido #{order.id}?</AlertDialogTitle>
                <AlertDialogDescription>
                  El pedido desaparecerá de la lista del conductor y del negocio, el conductor no podrá continuarlo y no contará como entrega realizada. Se guarda una copia interna solo para auditoría.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input data-testid="delete-order-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (opcional): el cliente no quiere el pedido" />
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-order-cancel">Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid="delete-order-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); remove(); }} className="bg-[#EE3B36] hover:bg-[#D93129]">{busy ? 'Eliminando...' : 'Eliminar pedido'}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const AdminOrders = () => {
  const { orders, deletedOrders } = useApp();
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [selected, setSelected] = useState(null);
  const [deleted, setDeleted] = useState([]);

  useEffect(() => {
    if (filter !== 'deleted') return;
    let alive = true;
    deletedOrders().then((list) => alive && setDeleted(list)).catch(() => alive && setDeleted([]));
    return () => { alive = false; };
  }, [filter, deletedOrders, orders]);

  const counts = useMemo(() => ({
    all: orders.length,
    awaiting_payment: orders.filter((o) => o.status === 'awaiting_payment').length,
    processing: orders.filter((o) => ['awaiting_driver', 'awaiting_business', 'pending', 'preparing', 'ready'].includes(o.status)).length,
    on_way: orders.filter((o) => o.status === 'on_way').length,
    completed: orders.filter((o) => o.status === 'completed').length,
    cancelled: orders.filter((o) => o.status === 'cancelled').length,
  }), [orders]);

  const rows = orders.filter((o) => {
    const f = filter === 'all' || (filter === 'processing' ? ['awaiting_driver', 'awaiting_business', 'pending', 'preparing', 'ready'].includes(o.status) : o.status === filter);
    const t = type === 'all' || o.type === type;
    const s = !q || o.customer.toLowerCase().includes(q.toLowerCase()) || o.id.toLowerCase().includes(q.toLowerCase()) || (o.restaurant || '').toLowerCase().includes(q.toLowerCase());
    return f && t && s;
  }).slice(0, 100);

  return (
    <div>
      <PageHeader title="Pedidos" subtitle="Gestiona todos los pedidos de la plataforma en tiempo real" actions={<Button variant="outline" className="gap-2" onClick={() => toast.success('Reporte exportado (demo)')}><Download size={16} /> Exportar</Button>} />
      <Card>
        <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-3 border-b border-gray-100">
          <FilterChips value={filter} onChange={setFilter} options={[
            { value: 'all', label: 'Todos', count: counts.all }, { value: 'awaiting_payment', label: 'Verificando pago', count: counts.awaiting_payment }, { value: 'processing', label: 'En proceso', count: counts.processing },
            { value: 'on_way', label: 'En camino', count: counts.on_way }, { value: 'completed', label: 'Completados', count: counts.completed }, { value: 'cancelled', label: 'Cancelados', count: counts.cancelled },
            { value: 'deleted', label: 'Eliminados' },
          ]} />
          <div className="lg:ml-auto flex items-center gap-2">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-9 w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los tipos</SelectItem><SelectItem value="Comida">Comida</SelectItem><SelectItem value="Mandadito">Mandadito</SelectItem><SelectItem value="Envío">Envío</SelectItem></SelectContent>
            </Select>
            <div className="relative"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pedido..." className="h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm w-[200px] focus:outline-none focus:border-[#EE3B36]" /></div>
          </div>
        </div>
        <div className="p-3">
          {filter === 'deleted' ? (
            <Table columns={['# Pedido', 'Cliente', 'Negocio / Origen', 'Total', 'Estado previo', 'Conductor', 'Motivo', 'Eliminado']} rows={deleted.filter((o) => !q || o.id.toLowerCase().includes(q.toLowerCase()) || (o.customer || '').toLowerCase().includes(q.toLowerCase()))} emptyText="No hay pedidos eliminados"
              renderRow={(o) => (
                <Tr key={o.id} data-testid={`deleted-row-${o.id}`}>
                  <Td className="font-semibold">#{o.id}</Td>
                  <Td>{o.customer}</Td>
                  <Td>{o.restaurant}</Td>
                  <Td className="font-semibold">{money(o.total)}</Td>
                  <Td><StatusBadge status={o.status} /></Td>
                  <Td className="text-gray-600">{o.driver || <span className="text-gray-400 italic">Sin asignar</span>}</Td>
                  <Td className="text-gray-600 max-w-[220px] [overflow-wrap:anywhere]">{o.deleteReason || '-'}</Td>
                  <Td className="text-gray-600 whitespace-nowrap">{o.deletedAt ? new Date(o.deletedAt).toLocaleString('es-HN') : '-'}</Td>
                </Tr>
              )} />
          ) : (
          <Table columns={['# Pedido', 'Cliente', 'Tipo', 'Restaurante / Origen', 'Total', 'Estado', 'Pago', 'Conductor', 'Fecha', 'Acciones']} rows={rows}
            renderRow={(o) => (
              <Tr key={o.id} data-testid={`orders-row-${o.id}`}>
                <Td className="font-semibold">#{o.id}</Td>
                <Td><span className="flex items-center gap-2"><Avatar src={o.avatar} name={o.customer} size={30} />{o.customer}</span></Td>
                <Td>{o.type}</Td><Td>{o.restaurant}</Td>
                <Td className="font-semibold">{money(o.total)}</Td>
                <Td><StatusBadge status={o.status} /></Td>
                <Td><PayBadge s={o.paymentStatus} /></Td>
                <Td className="text-gray-600">{o.driver || <span className="text-gray-400 italic">Sin asignar</span>}</Td>
                <Td className="text-gray-600 whitespace-nowrap">{o.date} {o.time}</Td>
                <Td><button data-testid={`view-order-${o.id}`} onClick={() => setSelected(o.id)} className="h-8 px-3 rounded-lg border border-gray-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-gray-50"><Eye size={14} /> Ver</button></Td>
              </Tr>
            )} />
          )}
        </div>
      </Card>
      <OrderDetail order={selected ? orders.find((o) => o.id === selected) : null} onClose={() => setSelected(null)} />
    </div>
  );
};

export default AdminOrders;
